/**
 * TypeScript types for DataSource management.
 * Aligned with backend schemas in api/src/datasources/schemas.py
 */

// =============================================================================
// Enums
// =============================================================================

export type ProviderType = 'git' | 's3' | 'sharepoint' | 'google_drive' | 'onedrive' | 'generic_git';
export type DataSourceStatus = 'active' | 'paused' | 'syncing' | 'error' | 'disconnected';
export type BindingType = 'collection';
export type SyncTrigger = 'scheduled' | 'manual' | 'webhook';
export type SyncRunStatus = 'running' | 'completed' | 'failed' | 'cancelled';
export type OAuthProvider = 'microsoft' | 'github' | 'google';

// =============================================================================
// Provider Configs
// =============================================================================

export interface GitProviderConfig {
  repo_url: string;
  branch: string;
  path: string;
}

export interface S3ProviderConfig {
  bucket: string;
  prefix: string;
  region: string;
  endpoint_url?: string | null;
}

export interface SharePointProviderConfig {
  site_url: string;
  library: string;
  folder: string;
}

export type ProviderConfig = GitProviderConfig | S3ProviderConfig | SharePointProviderConfig;

// =============================================================================
// Filter & Sync Config
// =============================================================================

export interface FilterConfig {
  include_extensions: string[];
  exclude_patterns: string[];
  max_file_size_bytes: number;
  max_depth: number;
}

export interface SyncConfig {
  interval_minutes: number;
  max_retries: number;
}

// =============================================================================
// DataSource
// =============================================================================

export interface DataSource {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  provider_type: ProviderType;
  provider_config: Record<string, unknown>;
  filter_config: Record<string, unknown>;
  sync_config: Record<string, unknown>;
  status: DataSourceStatus;
  error_message: string | null;
  consecutive_failures: number;
  bound_type: string | null;
  bound_collection_id: string | null;
  last_sync_at: string | null;
  next_sync_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// Create / Update
// =============================================================================

export interface DataSourceCreate {
  name: string;
  description?: string;
  provider_type: ProviderType;
  provider_config: Record<string, unknown>;
  credential?: Record<string, unknown>;
  oauth_credential_token?: string;
  filter_config?: FilterConfig;
  sync_config?: SyncConfig;
}

export interface DataSourceUpdate {
  name?: string;
  description?: string;
  provider_config?: Record<string, unknown>;
  credential?: Record<string, unknown>;
  filter_config?: FilterConfig;
  sync_config?: SyncConfig;
}

export interface BindRequest {
  collection_id: string;
}

// =============================================================================
// OAuth Types
// =============================================================================

export interface OAuthInitiateResponse {
  authorization_url: string;
  state: string;
  provider: string;
  expires_in: number;
}

export interface OAuthCallbackResponse {
  success: boolean;
  provider: string;
  credential_token: string;
  display_name: string | null;
  scopes_granted: string[];
  expires_at: string | null;
}

// =============================================================================
// Sync Runs
// =============================================================================

export interface SyncRun {
  id: string;
  source_id: string;
  trigger: SyncTrigger;
  status: SyncRunStatus;
  error_message: string | null;
  stats: Record<string, unknown>;
  started_at: string;
  completed_at: string | null;
}

// =============================================================================
// API Responses
// =============================================================================

export interface DataSourceListResponse {
  data: DataSource[];
  total: number;
}

export interface SyncRunListResponse {
  data: SyncRun[];
  total: number;
}

// =============================================================================
// Helpers
// =============================================================================

export const PROVIDER_LABELS: Record<ProviderType, string> = {
  git: 'Git Repository',
  s3: 'Amazon S3',
  sharepoint: 'SharePoint Online',
  google_drive: 'Google Drive',
  onedrive: 'Microsoft OneDrive',
  generic_git: 'Git Repository (Generic)',
};

export const PROVIDER_ICONS: Record<ProviderType, string> = {
  git: 'git-branch',
  s3: 'cloud',
  sharepoint: 'external-link',
  google_drive: 'hard-drive',
  onedrive: 'cloud',
  generic_git: 'git-branch',
};

export const STATUS_CONFIG: Record<DataSourceStatus, { color: string; animate?: boolean }> = {
  active: { color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
  paused: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' },
  syncing: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', animate: true },
  error: { color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
  disconnected: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
};

/** Maps datasource provider_type → OAuthProvider when supported */
export const PROVIDER_OAUTH_MAP: Partial<Record<ProviderType, OAuthProvider>> = {
  sharepoint: 'microsoft',
  onedrive: 'microsoft',
  git: 'github',
  google_drive: 'google',
};

/** Display names for OAuth providers */
export const OAUTH_DISPLAY_NAMES: Record<OAuthProvider, string> = {
  microsoft: 'Microsoft',
  github: 'GitHub',
  google: 'Google',
};

// =============================================================================
// Provider Manifest Types (from api/src/datasources/providers/manifest.py)
// =============================================================================

export type AuthMethod = 'oauth' | 'app_install' | 'credentials' | 'none';
export type WizardStep = 'connect' | 'browse' | 'configure' | 'review';
export type EnrollmentType = 'oauth_app_provisioned' | 'app_installation' | 'none';

export interface BrowseOperation {
  operation: string;
  label: string;
  description: string;
  input_schema: Record<string, unknown>;
  output_type: 'list' | 'tree';
  searchable: boolean;
  paginated: boolean;
  depends_on: string | null;
}

export interface ConfigField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'password';
  required: boolean;
  default: string | number | null;
  placeholder: string | null;
  hint: string | null;
  options: Record<string, unknown>[] | null;
  from_browse: string | null;
}

export interface CredentialField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'textarea' | 'select';
  required: boolean;
  placeholder: string | null;
  options: { value: string; label: string }[] | null;
  depends_on: Record<string, string> | null;
}

export interface EnrollmentCheck {
  type: EnrollmentType;
  oauth_provider: string | null;
  setup_label: string;
  setup_description: string;
  setup_url: string;
}

export interface ProviderManifest {
  provider_type: string;
  display_name: string;
  description: string;
  icon: string;
  category: string;
  auth_method: AuthMethod;
  oauth_provider: string | null;
  credential_fields: CredentialField[];
  wizard_steps: WizardStep[];
  browse_operations: BrowseOperation[];
  browse_component: string | null;
  config_fields: ConfigField[];
  config_defaults: Record<string, unknown>;
  default_filter_config: Record<string, unknown>;
  default_sync_config: Record<string, unknown>;
  name_template: string;
  enrollment_check: EnrollmentCheck | null;
}

// =============================================================================
// Enrollment (from api/src/datasources/schemas.py)
// =============================================================================

export interface EnrollmentAction {
  label: string;
  description: string;
  url: string;
  external_url: string | null;
}

export interface EnrichedProviderManifest extends ProviderManifest {
  enrolled: boolean;
  enrollment_status: 'ready' | 'needs_setup' | 'needs_install';
  enrollment_action: EnrollmentAction | null;
}

// =============================================================================
// Browse Types (from api/src/datasources/protocol.py)
// =============================================================================

export interface BrowseItem {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  metadata: Record<string, unknown>;
  selectable: boolean;
  expandable: boolean;
}

export interface BrowseResult {
  items: BrowseItem[];
  total_count: number | null;
  page: number | null;
  per_page: number | null;
  next_cursor: string | null;
}
